<template>
  <BaseModal
    :value="true"
    @input="$emit('close')"
    title="Edit Phone"
    id="infoauto-edit-phone"
  >
    <div class="flex flex-col p-4">
      <h1 class="text-lg font-bold mb-4">Edit Phone</h1>

      <!-- Disabled Warning Banner -->
      <div v-if="formData.disabled === 1" class="disabled-warning">
        <svg
          class="warning-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div>
          <strong>This phone is currently DISABLED</strong>
          <p class="text-sm">Will not be available for selection in orders</p>
        </div>
      </div>

      <form
        @submit.prevent="savePhone"
        class="flex flex-col gap-4"
        v-if="!loading"
      >
        <!-- Phone Status Section -->
        <div
          class="status-section"
          :class="{ 'status-disabled': !phoneEnabled }"
        >
          <svg
            v-if="!phoneEnabled"
            class="status-icon-warning"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <svg
            v-else
            class="status-icon-success"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 class="text-lg font-bold mb-2">Phone Status</h3>
          <label class="status-checkbox-label">
            <input
              v-model="phoneEnabled"
              type="checkbox"
              class="status-checkbox-input"
            />
            <div class="status-text">
              <strong class="text-xl">{{
                phoneEnabled ? "Enabled" : "Disabled"
              }}</strong>
              <span class="text-sm">{{
                phoneEnabled
                  ? "Active and available for orders"
                  : "Inactive, will not appear in order selection"
              }}</span>
            </div>
          </label>
        </div>

        <!-- Area Code -->
        <div class="form-field">
          <label class="form-label">Area Code</label>
          <input
            v-model.trim="formData.areaCode"
            placeholder="Enter area code (optional)"
            :maxlength="areaCodeMaxLength"
            class="regular full bordered"
          />
        </div>

        <!-- Phone Number -->
        <div class="form-field">
          <label class="form-label">Phone Number *</label>
          <input
            v-model="formData.number"
            placeholder="Enter phone number"
            required
            class="regular full bordered"
          />
        </div>

        <!-- Phone Type -->
        <div class="form-field">
          <label class="form-label">Phone Type</label>
          <select v-model="formData.tipo4" class="regular full bordered">
            <option
              v-for="phoneType in phoneTypes"
              :key="phoneType.value"
              :value="String(phoneType.value)"
              >{{ phoneType.label }}</option
            >
          </select>
        </div>

        <button type="submit" class="btn-dark-button w-40 mt-6">
          Save Changes
        </button>
      </form>
      <div v-else>
        <BaseSmallSpinner />
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { updatePhoneToCustomer } from "@/api/store/Infoauto.js";
import { PHONE_TYPES, AREA_CODE_MAX_LENGTH } from "@/constants/infoautoPersona";

export default {
  props: {
    customerId: {
      type: String,
      required: true
    },
    phone: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      formData: {
        telefonoId: "",
        areaCode: "",
        number: "",
        tipo4: "0",
        disabled: 0
      },
      phoneTypes: PHONE_TYPES,
      areaCodeMaxLength: AREA_CODE_MAX_LENGTH,
      loading: false
    };
  },
  computed: {
    phoneEnabled: {
      get() {
        return this.formData.disabled === 0;
      },
      set(value) {
        this.formData.disabled = value ? 0 : 1;
      }
    }
  },
  mounted() {
    this.initializeForm();
  },
  methods: {
    initializeForm() {
      this.formData = {
        telefonoId: this.phone.infoautoPhoneId,
        areaCode: this.phone.areaCode || "",
        number: this.phone.number,
        tipo4: this.phone.tipo4?.toString() || "0",
        disabled: this.phone.disabled
      };
    },
    async savePhone() {
      if (!this.formData.number) {
        this.$store.dispatch("alerts/addAlert", {
          type: "error",
          message: "Phone number is required"
        });
        return;
      }

      this.loading = true;

      try {
        const phoneData = {
          telefono_id: this.formData.telefonoId,
          numero: this.formData.number,
          cod_area: this.formData.areaCode,
          tipo_4: parseInt(this.formData.tipo4),
          disabled: this.formData.disabled
        };

        console.log("Updating phone:", phoneData);

        await updatePhoneToCustomer(this.customerId, phoneData);

        this.$store.dispatch("alerts/addAlert", {
          type: "success",
          message: "Phone updated successfully"
        });

        this.$emit("phone-updated");
      } catch (error) {
        console.error("Error updating phone:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to update phone";
        this.$store.dispatch("alerts/addAlert", {
          type: "error",
          message: errorMessage
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
#infoauto-edit-phone {
  .modal-content {
    width: 100%;
    max-width: 600px;
    height: auto;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}

.disabled-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background-color: #fef2f2;
  border-left: 4px solid #ef4444;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0.375rem;

  .warning-icon {
    width: 1.5rem;
    height: 1.5rem;
    color: #dc2626;
    flex-shrink: 0;
  }

  strong {
    color: #991b1b;
    font-size: 0.9375rem;
    display: block;
    margin-bottom: 0.25rem;
  }

  p {
    color: #7f1d1d;
    margin: 0;
  }
}

.status-section {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #86efac;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &.status-disabled {
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border-color: #fca5a5;
  }

  .status-icon-warning,
  .status-icon-success {
    width: 3rem;
    height: 3rem;
    margin-bottom: 0.75rem;
  }

  .status-icon-warning {
    color: #dc2626;
  }

  .status-icon-success {
    color: #16a34a;
  }

  h3 {
    margin: 0;
    margin-bottom: 1rem;
    color: #1f2937;
  }

  .status-checkbox-label {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    width: 100%;
    justify-content: center;
  }

  .status-checkbox-input {
    width: 1.5rem;
    height: 1.5rem;
    cursor: pointer;
  }

  .status-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    strong {
      font-size: 1.25rem;
      color: #111827;
    }

    span {
      color: #6b7280;
    }
  }
}
</style>
